'use client'

import AddForm from '@/components/add-form'
import GenericSelect from '@/components/select/generic-select'
import { Input, TextArea } from '@/components/ui/input'
import { endPoint } from '@/constant/end-point'
import { BugReportPriority, BugReportType } from '@/enum/global.enum'
import useApiMutation from '@/hooks/use-api-mutation'
import { useUnsavedChangesWarning } from '@/hooks/use-unsaved-changes-warning'
import type { IBugReport } from '@/interface/bug-report.interface'
import {
  createBugReportSchema,
  updateBugReportSchema,
  type CreateBugReportSchemaType,
  type UpdateBugReportSchemaType,
} from '@/schema/bug-report.schema'
import { createBugReportFakeData, updateBugReportFakeData } from '@/utils/fake-data'
import { handleFormSubmission } from '@/utils/form-submission'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { scrollToTop } from '@/utils/scroll-to-top'
import { FileUpload } from '@/components/ui/file-upload'

const AddBugReport = ({
  addOpen,
  setAddOpen,
  selectedValue,
  modalMode = false,
  onAddSuccess,
}: {
  addOpen: boolean
  setAddOpen: (value: boolean) => void
  selectedValue?: IBugReport | null
  setSelectedValue?: (data: IBugReport | null) => void
  modalMode?: boolean
  onAddSuccess?: (id: string) => void
}) => {
  const { createMutation, updateMutation } = useApiMutation<CreateBugReportSchemaType, UpdateBugReportSchemaType>({
    endpoint: endPoint.bugReport,
    contentType: 'multipart/form-data',
  })

  const defaultValues = {
    title: '',
    description: '',
    type: BugReportType.BUG,
    priority: BugReportPriority.LOW,
    pageUrl: '',
    attachmentUrl: '',
  }

  const {
    register,
    reset,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<CreateBugReportSchemaType | UpdateBugReportSchemaType>({
    resolver: zodResolver(selectedValue ? updateBugReportSchema : createBugReportSchema),
    defaultValues,
  })

  useUnsavedChangesWarning({ when: addOpen && isDirty && !isSubmitting })
  const onSubmit = useCallback(
    async (data: CreateBugReportSchemaType | UpdateBugReportSchemaType) => {
      const req = {
        title: data.title,
        description: data.description,
        type: data.type,
        priority: data.priority,
        pageUrl: data.pageUrl,
        attachmentUrl: data?.attachmentUrl?.[0] as File,
      }

      const createData: CreateBugReportSchemaType = {
        ...req,
      } as CreateBugReportSchemaType

      const updateData: UpdateBugReportSchemaType = {
        ...req,
        id: selectedValue?.id ?? '',
      } as UpdateBugReportSchemaType

      await handleFormSubmission({
        createMutation: async () => {
          const response = await createMutation.mutateAsync(createData)
          if (onAddSuccess) onAddSuccess((response?.data as unknown as { id: string })?.id as string)
        },
        updateMutation: async () => {
          await updateMutation.mutateAsync({
            ...updateData,
            id: selectedValue?.id ?? '',
          })
        },
        reset: () => reset(defaultValues),
        setOpen: setAddOpen,
        isUpdate: !!selectedValue,
        successMessageTitle: 'Bug report submitted successfully',
        successMessageDescription: 'Your bug report has been submitted successfully. Please wait for the review.',
      })
    },
    [createMutation, updateMutation, selectedValue, reset, setAddOpen, onAddSuccess]
  )

  useEffect(() => {
    if (selectedValue)
      reset({
        id: selectedValue.id,
        title: selectedValue.title ?? '',
        description: selectedValue.description ?? '',
        type: selectedValue.type ?? BugReportType.BUG,
        priority: selectedValue.priority ?? BugReportPriority.LOW,
        pageUrl: selectedValue.pageUrl ?? '',
        attachmentUrl: selectedValue.attachmentUrl?.trim() ?? '',
      })
  }, [selectedValue])

  // Prefill with the page the user was on when they opened this form (editable) - not for update mode
  useEffect(() => {
    if (addOpen && !selectedValue) setValue('pageUrl', window.location.href)
  }, [addOpen, selectedValue, setValue])

  return (
    <AddForm
      addOpen={addOpen}
      setAddOpen={setAddOpen}
      onSubmit={handleSubmit(onSubmit, () => {
        scrollToTop()
      })}
      title={selectedValue ? 'Update Bug Report' : 'Submit Bug Report'}
      isSubmitting={isSubmitting}
      update={!!selectedValue}
      fakeFiller={() => {
        reset(selectedValue ? updateBugReportFakeData() : createBugReportFakeData())
      }}
      onHandleClose={() => {
        reset(defaultValues)
      }}
      modalMode={modalMode}
    >
      <div className='space-y-4'>
        <Input
          label='Title'
          required
          error={errors?.title?.message}
          placeholder='Enter Title (e.g. System is not working)'
          autoFocus
          {...register('title')}
        />
        <TextArea
          required
          label='Description'
          error={errors?.description?.message}
          placeholder='Enter Description (e.g. The system is not working as expected)'
          {...register('description')}
          maxLength={5000}
          helpertext={`${watch('description')?.length || 0} / 5000 characters`}
        />
        <div className='grid md:grid-cols-2 grid-cols-1 gap-4'>
          <GenericSelect
            label={'Type'}
            value={watch('type') as BugReportType}
            error={errors.type?.message}
            handleChange={async (e) => {
              setValue('type', e as BugReportType)
              trigger('type')
            }}
            options={BugReportType}
            required
            placeholder='Select Type'
          />
          <GenericSelect
            label={'Priority'}
            value={watch('priority') as BugReportPriority}
            error={errors.priority?.message}
            handleChange={async (e) => {
              setValue('priority', e as BugReportPriority)
              trigger('priority')
            }}
            options={BugReportPriority}
            required
            placeholder='Select Priority'
          />
        </div>

        <Input label='Page URL' error={errors?.pageUrl?.message} placeholder='https://...' {...register('pageUrl')} />
        <FileUpload
          label='Supporting Screenshot'
          error={errors?.attachmentUrl?.message as string}
          multiple={false}
          accept='image/jpeg,image/jpg,image/png'
          maxSize={5 * 1024 * 1024} // 5MB
          {...register('attachmentUrl')}
          value={watch('attachmentUrl')}
          variant='sm'
        />
      </div>
    </AddForm>
  )
}

export default AddBugReport
